import { Check, ChevronDown, Search } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { useListbox } from './useListbox'

export interface SelectOption {
  value: string
  label: string
  sub?: string
  icon?: ReactNode
  group?: string
}

export interface SelectProps {
  value: string | null
  options: SelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  searchable?: boolean
  renderValue?: (option: SelectOption) => ReactNode
  emptyText?: ReactNode
  /** Accessible name when no visible label points at the select. */
  label?: string
  /** Id of a visible label (for example a Field label). */
  labelledBy?: string
  disabled?: boolean
}

/**
 * Searchable, grouped select (mockup `ui.tsx` → `Select`), as a select-only combobox: the trigger keeps focus and
 * `aria-activedescendant` follows the highlighted option. Keyboard: arrows, Home/End, type-ahead, Enter, Escape.
 */
export function Select({
  value,
  options,
  onChange,
  placeholder,
  searchable,
  renderValue,
  emptyText = 'No matches',
  label,
  labelledBy,
  disabled,
}: SelectProps) {
  const [open, setOpenState] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const baseId = useId()
  const listId = `${baseId}-list`
  const optionId = (i: number) => `${baseId}-opt-${String(i)}`

  const filtered = options.filter((o) => !q || `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(q.toLowerCase()))
  const selectedIndex = filtered.findIndex((o) => o.value === value)

  const setOpen = (next: boolean) => {
    setOpenState(next)
    if (!next) {
      setQ('')
      triggerRef.current?.focus()
    }
  }

  const choose = (o: SelectOption) => {
    onChange(o.value)
    setOpen(false)
  }

  const { active, setActive, onKeyDown } = useListbox({
    items: filtered,
    open,
    setOpen,
    selectedIndex,
    onChoose: choose,
    typeAhead: !searchable,
    homeEnd: !searchable,
  })

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpenState(false)
        setQ('')
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
    }
  }, [open])

  useEffect(() => {
    if (open && active >= 0) document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' })
    // optionId is derived from the stable baseId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active])

  const current = options.find((o) => o.value === value)
  const activeId = open && active >= 0 && active < filtered.length ? optionId(active) : undefined

  // A group heading before the first option of each group.
  const headed = filtered.map((o, i) => ({
    o,
    header: o.group !== undefined && o.group !== filtered[i - 1]?.group ? o.group : undefined,
  }))
  return (
    <div className="select" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        className="select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={searchable ? undefined : activeId}
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        disabled={disabled}
        onClick={() => {
          if (open) setOpen(false)
          else {
            setOpenState(true)
            setActive(selectedIndex >= 0 ? selectedIndex : 0)
          }
        }}
        onKeyDown={searchable && open ? undefined : onKeyDown}
      >
        {current ? (
          renderValue ? (
            renderValue(current)
          ) : (
            <>
              {current.icon}
              <span className="ellipsis">{current.label}</span>
            </>
          )
        ) : (
          <span className="placeholder">{placeholder}</span>
        )}
        <ChevronDown aria-hidden />
      </button>
      {open && (
        <div className="select-menu">
          {searchable && (
            <div className="select-search input-wrap">
              <Search aria-hidden />
              <input
                // The search box takes focus when the menu opens (as in the mockup).
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                className="input"
                placeholder="Search…"
                role="combobox"
                aria-label="Search options"
                aria-autocomplete="list"
                aria-expanded
                aria-controls={listId}
                aria-activedescendant={activeId}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value)
                  setActive(0)
                }}
                onKeyDown={onKeyDown}
              />
            </div>
          )}
          {filtered.length === 0 && (
            <div className="select-empty" role="status" id={listId}>
              {emptyText}
            </div>
          )}
          {filtered.length > 0 && (
            <div role="listbox" id={listId} aria-label={label ?? 'Options'}>
              {headed.map(({ o, header }, i) => {
                return (
                  <div key={o.value} role="presentation">
                    {header !== undefined && (
                      <div className="select-group" role="presentation">
                        {header}
                      </div>
                    )}
                    {/* Keyboard is handled on the combobox (aria-activedescendant); options are not tab stops. */}
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                    <div
                      id={optionId(i)}
                      className={`select-option${i === active ? ' active' : ''}`}
                      role="option"
                      tabIndex={-1}
                      aria-selected={o.value === value}
                      onMouseDown={(e) => {
                        e.preventDefault()
                      }}
                      onMouseEnter={() => {
                        setActive(i)
                      }}
                      onClick={() => {
                        choose(o)
                      }}
                    >
                      {o.icon}
                      <span className="grow">
                        <span className="ellipsis">{o.label}</span>
                        {o.sub && <span className="hint">{o.sub}</span>}
                      </span>
                      {o.value === value && <Check className="check" aria-hidden />}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
